import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useEditorShortcuts, groupShortcuts, type EditorShortcut } from '../hooks/use-editor-shortcuts';
import { useShortcutsStore } from '../stores/shortcuts-store';
import { ShortcutsHelpButton } from './shortcuts-help';
import { motion, AnimatePresence, useDragControls } from 'motion/react';
import { useAppStore } from '../stores/app-store';
import { useCommentsStore, useOpenComments, useResolvedComments } from '../stores/comments-store';
import { useAuthStore } from '../stores/auth-store';
import { useAIStore } from '../stores/ai-store';
import { useCollaboration } from '../hooks/use-collaboration';
import { ChapterEditor } from './chapter-editor';
import { BookMetadataModal } from './book-metadata-modal';
import { WritingGoalModal } from './writing-goal-modal';
import { FindReplace } from './find-replace';
import { ChapterNotesModal } from './chapter-notes-modal';
import { AIChat } from './ai';
import {
  Share2,
  Download,
  X,
  StickyNote,
  Target,
  Check,
  MessageSquare,
  Send,
  CornerDownRight,
  Sparkles,
  History,
  MoreVertical,
} from 'lucide-react';
import { GiScrollQuill, GiSpellBook, GiScrollUnfurled, GiArchiveResearch } from './icons/game-icons';
import { ShareModal } from './share-modal';
import { VersionHistoryPanel } from './version-history-panel';
import { Drawer, DrawerContent, DrawerTitle } from './ui/drawer';
import { TextSettingsPopover } from './text-settings-popover';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { countWords } from '../utils/word-count';
import { cn } from '../lib/utils';
import { LEVEL_XP, totalWordsOf, writerLevelOf } from '../lib/writer-level';
import { useIsMobile } from '@/hooks/use-mobile';
import { StoryBibleView } from './story-bible-view';
import { OutlineBoardView } from './outline-board-view';
import type { Editor } from '@tiptap/react';

// Shared style for the mobile editor toolbar buttons (larger touch targets).
const mobileToolBtn =
  'shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg text-muted-foreground hover:text-foreground active:bg-muted/80 transition-colors';

// Workspace tabs: the 3 in-place views inside the editor workspace.
// 'exportar' is not a tab state — it navigates to /app/export/:projectId.
type WorkspaceTab = 'escrever' | 'biblia' | 'estrutura';

export function EditorView() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);
  const [metadataOpen, setMetadataOpen] = useState(false);
  const [goalModalOpen, setGoalModalOpen] = useState(false);
  const [findReplaceOpen, setFindReplaceOpen] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'unsaved'>('saved');
  const [activeTab, setActiveTab] = useState<'comments' | 'suggestions'>('comments');
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [focusMode, setFocusMode] = useState(false);
  // cerimônia rápida: a íris fecha ao entrar no modo foco
  useEffect(() => {
    if (!focusMode) return;
    window.dispatchEvent(new CustomEvent('reformu:moment', { detail: { type: 'iris' } }));
  }, [focusMode]);

  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  // Workspace tab: read initial value from ?ws= query param (used by deep-link redirects).
  const [searchParams] = useSearchParams();
  const [workspaceTab, setWorkspaceTab] = useState<WorkspaceTab>(() => {
    const ws = searchParams.get('ws');
    if (ws === 'bible') return 'biblia';
    if (ws === 'outline') return 'estrutura';
    return 'escrever';
  });
  const editorRef = useRef<Editor | null>(null);
  // Drag controls for mobile bottom-sheet dismissal (one per sheet)
  const commentsDragControls = useDragControls();
  const findReplaceDragControls = useDragControls();

  const { user } = useAuthStore();

  const {
    projects,
    currentChapterId,
    updateChapterDebounced,
    settings,
    updateSettings,
    addChapter,
    updateChapter,
    deleteChapter,
    reorderChapters,
    setCurrentChapter,
    setCurrentProject,
  } = useAppStore();

  const {
    fetchComments,
    addComment,
    resolveComment,
    clearComments,
  } = useCommentsStore();

  const openComments = useOpenComments();
  const resolvedComments = useResolvedComments();

  // AI Store - for per-project chat history and selection sync
  const setAICurrentProject = useAIStore((s) => s.setCurrentProject);
  const setAISelection = useAIStore((s) => s.setSelection);
  const clearAISelection = useAIStore((s) => s.clearSelection);
  const aiSelectionRange = useAIStore((s) => s.selectionRange);
  const aiCurrentSelection = useAIStore((s) => s.currentSelection);

  // Get project from URL param
  const project = projects.find((p) => p.id === projectId);
  const chapter = project?.chapters.find((c) => c.id === currentChapterId);

  // Manga projects use the dedicated canvas editor, not the prose editor.
  useEffect(() => {
    if (project && (project as unknown as { kind?: string }).kind === 'manga') {
      navigate(`/app/manga/${project.id}`, { replace: true });
    }
  }, [project, navigate]);

  // Determine ownership — the API enriches projects with isOwner when fetched
  const isOwner = (project as unknown as { isOwner?: boolean })?.isOwner !== false;

  // Collaboration: only active when project+chapter are known and user is available
  const collaborationInfo = useCollaboration({
    projectId: projectId ?? '',
    chapterId: currentChapterId ?? '',
    userId: user?.id ?? '',
    userName: user?.name ?? 'Anonymous',
    isOwner,
  });

// cerimônia: página limpa — os comentários abertos zeraram NESTA sessão
  const prevOpenCommentsRef = useRef(0);
  useEffect(() => {
    const prev = prevOpenCommentsRef.current;
    prevOpenCommentsRef.current = openComments.length;
    if (prev > 0 && openComments.length === 0) {
      window.dispatchEvent(new CustomEvent('reformu:moment', { detail: { type: 'cleanpage' } }));
    }
  }, [openComments.length]);

  // cerimônia: companhia — alguém entrou pra escrever junto (1x por projeto)
  const remoteCount = collaborationInfo?.remoteUsers.length ?? 0;
  useEffect(() => {
    if (remoteCount === 0 || !projectId) return;
    const key = `moment-companion-${projectId}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
    const first = collaborationInfo?.remoteUsers[0]?.name || '';
    window.dispatchEvent(new CustomEvent('reformu:moment', { detail: { type: 'companion', name: first } }));
  }, [remoteCount, projectId]);

  // Sync project from URL with store and handle missing project
  // This effect only runs on project/projectId changes, NOT on currentChapterId changes
  useEffect(() => {
    if (projectId && project) {
      setCurrentProject(projectId);
      setAICurrentProject(projectId); // Set AI chat context to this project
    } else if (!project && projectId) {
      // Project not found, redirect to projects
      navigate('/app/projects');
    }
  }, [projectId, project, setCurrentProject, setAICurrentProject, navigate]);

  // Set initial chapter only if no valid chapter is selected
  useEffect(() => {
    if (!project || !projectId) return;

    const chapterExists = currentChapterId && project.chapters.some(c => c.id === currentChapterId);
    if (!chapterExists && project.chapters.length > 0) {
      setCurrentChapter(project.chapters[0].id);
    }
  }, [projectId, project?.chapters.length]); // Only trigger when project or chapter count changes

  // Load comments when chapter changes
  useEffect(() => {
    if (currentChapterId) {
      fetchComments(currentChapterId);
    }
    return () => clearComments();
  }, [currentChapterId, fetchComments, clearComments]);

  // Calculate chapter number (only for 'chapter' type sections)
  const chapterNumber = chapter && project
    ? project.chapters
        .filter(c => c.sectionType === 'chapter')
        .findIndex(c => c.id === chapter.id) + 1
    : undefined;

  const totalWords = project?.chapters.reduce(
    (sum, ch) => sum + countWords(ch.content),
    0
  ) ?? 0;

  // Barra de status da demo: nível do escritor + nome do efeito de escrita
  const writerLvl = writerLevelOf(totalWordsOf(projects));
  const writingEffectLabel = ({
    petals: t('textSettings.effectPetals', 'Pétalas'),
    stardust: t('textSettings.effectStardust', 'Poeira Estelar'),
    inkrain: t('textSettings.effectInkRain', 'Chuva de Tinta'),
    sparks: t('textSettings.effectSparks', 'Faíscas de Choque'),
    glitch: t('textSettings.effectGlitch', 'Faísca Glitch'),
    mist: t('textSettings.effectMist', 'Névoa Cósmica'),
    fire: t('textSettings.effectFire', 'Lareira em Chamas'),
    fireflies: t('textSettings.effectFireflies', 'Vagalumes'),
    neon: t('textSettings.effectNeon', 'Neon Elétrico'),
    glyphs: t('textSettings.effectGlyphs', 'Eco Literário'),
  } as Record<string, string>)[settings.writingEffect] ?? '';

  useEffect(() => {
    if (saveStatus === 'unsaved' && settings.autoSave) {
      const timer = setTimeout(() => {
        setSaveStatus('saved');
      }, settings.autoSaveInterval * 1000);
      return () => clearTimeout(timer);
    }
  }, [saveStatus, settings.autoSave, settings.autoSaveInterval]);

  // Handle adding a new comment
  const handleAddComment = useCallback(async () => {
    if (!currentChapterId || !newComment.trim()) return;

    await addComment({
      chapterId: currentChapterId,
      content: newComment.trim(),
      type: 'comment',
    });
    setNewComment('');
  }, [currentChapterId, newComment, addComment]);

  // Handle replying to a comment
  const handleReply = useCallback(async (parentId: string) => {
    if (!currentChapterId || !replyText.trim()) return;

    await addComment({
      chapterId: currentChapterId,
      content: replyText.trim(),
      type: 'comment',
      parentId,
    });
    setReplyText('');
    setReplyingTo(null);
  }, [currentChapterId, replyText, addComment]);

  // Get user initials
  const getUserInitials = (name: string) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  };

  // Format time ago
  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('editorView.timeAgoNow', 'agora');
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays === 1) return t('editorView.timeAgoYesterday', 'Ontem');
    return t('editorView.timeAgoDays', 'há {{days}}d', { days: diffDays });
  };

  const handleContentChange = useCallback(
    (content: string) => {
      if (!project || !currentChapterId) return;

      setSaveStatus('saving');
      updateChapterDebounced(project.id, currentChapterId, {
        content,
        wordCount: countWords(content),
      });

      setTimeout(() => setSaveStatus('saved'), 500);
    },
    [project, currentChapterId, updateChapterDebounced]
  );

  const handleTitleChange = useCallback(
    (title: string) => {
      if (!project || !currentChapterId) return;
      updateChapterDebounced(project.id, currentChapterId, { title });
    },
    [project, currentChapterId, updateChapterDebounced]
  );


  // Force-save handler: mark as saved and flush any pending debounce
  const handleForceSave = useCallback(() => {
    if (!project || !currentChapterId) return;
    const chapter = project.chapters.find((c) => c.id === currentChapterId);
    if (!chapter) return;
    setSaveStatus('saving');
    // updateChapter (non-debounced) writes immediately
    updateChapter(project.id, currentChapterId, {
      content: chapter.content,
      wordCount: chapter.wordCount,
    });
    setTimeout(() => setSaveStatus('saved'), 300);
  }, [project, currentChapterId, updateChapter]);

  const handleEditorReady = useCallback((editor: Editor) => {
    editorRef.current = editor;
  }, []);

  // Handle AI Chat action (replace/insert text) with animation
  const handleApplyAIAction = useCallback((action: any) => {
    const editor = editorRef.current;

    // Only handle text actions here
    if (action.type !== 'replace' && action.type !== 'insert') {
      return;
    }

    if (!editor || !action.newText) return;

    // Color definitions for AI changes
    const removeColor = 'rgba(239, 68, 68, 0.25)'; // Red for removing
    const addColor = 'rgba(34, 197, 94, 0.3)'; // Green for adding

    if (action.type === 'replace' && action.searchText) {
      const searchText = action.searchText.trim();
      const doc = editor.state.doc;

      let foundFrom: number;
      let foundTo: number;

      // Check if we have a stored selection range and the searchText matches
      // This ensures we replace the EXACT selected text, not other similar text
      if (aiSelectionRange && aiCurrentSelection.trim() === searchText) {
        // Use the stored selection range for precise replacement
        foundFrom = aiSelectionRange.from;
        foundTo = aiSelectionRange.to;
      } else {
        // Fallback: find text by searching the document
        // Build a map of character positions to ProseMirror positions
        const positions: Array<{ char: number; pmPos: number }> = [];
        let charIndex = 0;

        doc.descendants((node, pos) => {
          if (node.isText) {
            const text = node.text || '';
            for (let i = 0; i < text.length; i++) {
              positions.push({ char: charIndex + i, pmPos: pos + i });
            }
            charIndex += text.length;
          }
        });

        // Get full document text (only text content, no structure)
        const fullText = doc.textContent;
        const searchIndex = fullText.indexOf(searchText);

        if (searchIndex === -1) {
          console.warn('Text not found in document:', searchText.substring(0, 50));
          return;
        }

        // Map string positions to ProseMirror positions
        const startPos = positions.find(p => p.char === searchIndex);
        const endPos = positions.find(p => p.char === searchIndex + searchText.length - 1);

        if (!startPos || !endPos) {
          console.warn('Could not map text positions');
          return;
        }

        foundFrom = startPos.pmPos;
        foundTo = endPos.pmPos + 1; // +1 because selection end is exclusive
      }

      // Step 1: Highlight old text with red (removing indicator)
      editor.chain()
        .focus()
        .setTextSelection({ from: foundFrom, to: foundTo })
        .setHighlight({ color: removeColor })
        .run();

      // Step 2: After brief delay, replace text and highlight green
      setTimeout(() => {
        editor.chain()
          .focus()
          .setTextSelection({ from: foundFrom, to: foundTo })
          .unsetHighlight()
          .insertContent(action.newText!)
          .run();

        // Apply green highlight to new text
        const newTo = foundFrom + action.newText!.length;
        editor.chain()
          .focus()
          .setTextSelection({ from: foundFrom, to: newTo })
          .setHighlight({ color: addColor })
          .run();

        // Step 3: Remove highlight after animation
        setTimeout(() => {
          editor.chain()
            .focus()
            .setTextSelection({ from: foundFrom, to: newTo })
            .unsetHighlight()
            .run();
        }, 800);
      }, 400);
    } else if (action.type === 'insert') {
      // Insert at cursor position with green highlight
      const { from } = editor.state.selection;
      editor.chain()
        .focus()
        .insertContent(action.newText)
        .run();

      // Apply highlight to inserted text
      const newTo = from + action.newText.length;
      editor.chain()
        .focus()
        .setTextSelection({ from, to: newTo })
        .setHighlight({ color: addColor })
        .run();

      // Remove highlight after animation
      setTimeout(() => {
        editor.chain()
          .focus()
          .setTextSelection({ from, to: newTo })
          .unsetHighlight()
          .run();
      }, 800);
    }
  }, []);

  // Handle selection change from editor (real-time sync)
  const handleEditorSelectionChange = useCallback(
    (text: string, range: { from: number; to: number } | null) => {
      if (text && range) {
        setSelectedText(text);
        // Sync with AI store for chat integration (include range for precise replacement)
        setAISelection(text, range);
      } else {
        setSelectedText('');
        clearAISelection();
      }
    },
    [setAISelection, clearAISelection]
  );

  // Get current selection from editor and sync with AI store (manual trigger)
  const handleSelectionChange = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const { from, to } = editor.state.selection;
    if (from !== to) {
      const text = editor.state.doc.textBetween(from, to, ' ');
      setSelectedText(text);
      // Sync with AI store for chat integration
      setAISelection(text);
    } else {
      setSelectedText('');
      clearAISelection();
    }
  }, [setAISelection, clearAISelection]);

  // The mobile tools row was removed — its actions now live inside the gear
  // menu (EditorSettingsMenu, rendered by app-shell). Since that menu is a
  // sibling component, it reaches this view via window events instead of props.
  useEffect(() => {
    const openFind = () => setFindReplaceOpen(true);
    const openGoal = () => setGoalModalOpen(true);
    const openVersions = () => setVersionHistoryOpen(true);
    const openNotes = () => setNotesModalOpen(true);
    const toggleAiChat = () => {
      handleSelectionChange();
      setAiChatOpen((v) => !v);
    };
    const openShare = () => setShareModalOpen(true);
    const publish = () => { if (projectId) navigate(`/app/export/${projectId}`); };

    window.addEventListener('editor-open-find', openFind);
    window.addEventListener('editor-open-goal', openGoal);
    window.addEventListener('editor-open-versions', openVersions);
    window.addEventListener('editor-open-notes', openNotes);
    window.addEventListener('editor-toggle-aichat', toggleAiChat);
    window.addEventListener('editor-open-share', openShare);
    window.addEventListener('editor-publish', publish);

    return () => {
      window.removeEventListener('editor-open-find', openFind);
      window.removeEventListener('editor-open-goal', openGoal);
      window.removeEventListener('editor-open-versions', openVersions);
      window.removeEventListener('editor-open-notes', openNotes);
      window.removeEventListener('editor-toggle-aichat', toggleAiChat);
      window.removeEventListener('editor-open-share', openShare);
      window.removeEventListener('editor-publish', publish);
    };
  }, [handleSelectionChange, projectId, navigate]);

  const handleNotesUpdate = useCallback(
    (notes: string) => {
      if (!project || !currentChapterId) return;
      updateChapterDebounced(project.id, currentChapterId, { notes });
    },
    [project, currentChapterId, updateChapterDebounced]
  );

  // When a version is restored, update the chapter in the store and reload editor content
  const handleVersionRestored = useCallback(
    (newContent: string) => {
      if (!project || !currentChapterId) return;
      updateChapter(project.id, currentChapterId, { content: newContent });
    },
    [project, currentChapterId, updateChapter]
  );

  const handleImageChange = useCallback(
    (image: string | undefined) => {
      if (!project || !currentChapterId) return;
      updateChapterDebounced(project.id, currentChapterId, { image });
    },
    [project, currentChapterId, updateChapterDebounced]
  );

  // Switch workspace tab; close any open overlays when leaving the writing view.
  const handleWorkspaceTabChange = useCallback((tab: WorkspaceTab) => {
    if (tab !== 'escrever') {
      setAiChatOpen(false);
      setRightSidebarOpen(false);
      setVersionHistoryOpen(false);
      setFindReplaceOpen(false);
    }
    setWorkspaceTab(tab);
  }, []);

  // As abas da casca (win-top global) trocam a vista via ?ws= — sincroniza aqui
  useEffect(() => {
    const ws = searchParams.get('ws');
    const next: WorkspaceTab = ws === 'bible' ? 'biblia' : ws === 'outline' ? 'estrutura' : 'escrever';
    setWorkspaceTab((prev) => {
      if (prev === next) return prev;
      if (next !== 'escrever') {
        setAiChatOpen(false);
        setRightSidebarOpen(false);
        setVersionHistoryOpen(false);
        setFindReplaceOpen(false);
      }
      return next;
    });
  }, [searchParams]);

  // AI structural action handlers
  const handleCreateChapter = useCallback(
    async (data: {
      title: string;
      content?: string;
      position?: 'after_current' | 'before_current' | 'end' | 'start';
      sectionType?: string;
    }) => {
      if (!project) return;

      // Pass content directly to addChapter
      const newChapterId = await addChapter(
        project.id,
        data.title,
        (data.sectionType as any) || 'chapter',
        data.content || ''
      );

      // Handle position logic (simplified - just set as current for now)
      setCurrentChapter(newChapterId);
    },
    [project, addChapter, setCurrentChapter]
  );

  const handleUpdateChapterTitle = useCallback(
    async (id: string, data: { title?: string }) => {
      if (!project) return;
      updateChapter(project.id, id, data);
    },
    [project, updateChapter]
  );

  const handleDeleteChapterById = useCallback(
    async (id: string) => {
      if (!project) return;
      await deleteChapter(project.id, id);
    },
    [project, deleteChapter]
  );

  const handleReorderChaptersById = useCallback(
    async (ids: string[]) => {
      if (!project) return;
      // Calculate reorder indices from IDs
      const currentChapters = project.chapters;
      const newOrder = ids.map((id) => currentChapters.findIndex((c) => c.id === id));
      // This is simplified - actual reorder logic would need proper index mapping
      // For now, we'll just call the API with the IDs
      await reorderChapters(project.id, 0, newOrder[0] || 0);
    },
    [project, reorderChapters]
  );

  // ── Structural keyboard shortcuts — creation/navigation only. Text formatting
  // (Ctrl+B/I/U…) stays with TipTap; save/find/focus stay in the other hook
  // above. Everything here uses mod/alt combos so it coexists with typing and
  // never shadows the rich-text editor. Published to the shared help overlay. ──
  const handleNewChapter = useCallback(async () => {
    if (!project) return;
    const id = await addChapter(project.id, t('editor.newChapterTitle', 'Novo capítulo'), 'chapter', '');
    setCurrentChapter(id);
  }, [project, addChapter, setCurrentChapter, t]);
  const handleDuplicateChapter = useCallback(async () => {
    if (!project || !chapter) return;
    const id = await addChapter(project.id, `${chapter.title} ${t('editor.copySuffix', '(cópia)')}`, chapter.sectionType, chapter.content);
    setCurrentChapter(id);
  }, [project, chapter, addChapter, setCurrentChapter, t]);
  const goToChapterDelta = useCallback((delta: 1 | -1) => {
    if (!project) return;
    const list = project.chapters;
    const idx = list.findIndex((c) => c.id === currentChapterId);
    if (idx === -1) return;
    const ni = idx + delta;
    if (ni < 0 || ni >= list.length) return;
    setCurrentChapter(list[ni].id);
  }, [project, currentChapterId, setCurrentChapter]);

  const bookShortcuts: EditorShortcut[] = [
    // Creation
    { id: 'bk-new-chapter', combo: 'alt+n', group: 'create', when: () => !!project, description: t('shortcuts.bkNewChapter', 'Novo capítulo'), handler: () => { void handleNewChapter(); } },
    { id: 'bk-dup-chapter', combo: 'alt+d', group: 'create', when: () => !!project && !!chapter, description: t('shortcuts.bkDupChapter', 'Duplicar capítulo'), handler: () => { void handleDuplicateChapter(); } },
    // Navigation
    { id: 'bk-prev-chapter', combo: 'alt+up', group: 'navigate', when: () => !!project, description: t('shortcuts.bkPrevChapter', 'Capítulo anterior'), handler: () => goToChapterDelta(-1) },
    { id: 'bk-next-chapter', combo: 'alt+down', group: 'navigate', when: () => !!project, description: t('shortcuts.bkNextChapter', 'Próximo capítulo'), handler: () => goToChapterDelta(1) },
    { id: 'bk-ai', combo: 'mod+shift+g', group: 'navigate', when: () => !!project && !!chapter, description: t('shortcuts.bkAi', 'Assistente de IA'), handler: () => { handleSelectionChange(); setAiChatOpen((v) => !v); } },
    { id: 'bk-export', combo: 'mod+shift+e', group: 'navigate', when: () => !!projectId, description: t('shortcuts.bkExport', 'Publicar / exportar'), handler: () => navigate(`/app/export/${projectId}`) },
    { id: 'bk-focus', combo: ['f11', 'mod+shift+f'], group: 'navigate', description: t('shortcuts.bkFocus', 'Modo foco (tela cheia)'), handler: () => setFocusMode((v) => !v), allowInInput: true },
    // Edit — migrated from the legacy hook so there is ONE keyboard system.
    { id: 'bk-save', combo: 'mod+s', group: 'edit', description: t('shortcuts.bkSave', 'Salvar agora'), handler: () => handleForceSave(), allowInInput: true },
    { id: 'bk-find', combo: 'mod+f', group: 'edit', description: t('shortcuts.bkFind', 'Localizar e substituir'), handler: () => setFindReplaceOpen(true), allowInInput: true },
    { id: 'bk-esc', combo: 'esc', group: 'edit', description: '__hidden', allowInInput: true, handler: () => { if (focusMode) setFocusMode(false); else setFindReplaceOpen(false); } },
    // Help
    { id: 'bk-help', combo: '?', group: 'help', allowInModal: true, description: t('shortcuts.showHelp', 'Mostrar esta ajuda'), handler: () => window.dispatchEvent(new CustomEvent('reformu:shortcuts-open')) },
  ];
  useEditorShortcuts(bookShortcuts);
  const setShortcutsContext = useShortcutsStore((s) => s.setContext);
  const clearShortcutsContext = useShortcutsStore((s) => s.clearContext);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const bookHelpSections = useMemo(() => groupShortcuts(bookShortcuts.filter((s) => s.description !== '__hidden')), [t]);
  useEffect(() => {
    setShortcutsContext(bookHelpSections, t('shortcuts.bookTitle', 'Atalhos — Editor de livro'));
    return () => clearShortcutsContext();
  }, [bookHelpSections, setShortcutsContext, clearShortcutsContext, t]);

  if (!project) {
    return (
      <div className="flex-1 flex items-center justify-center bg-background">
        <p className="text-muted-foreground">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      {/* Editor Topbar — vive no fundo da janela (casca), como na demo */}
      <header className="flex-shrink-0 bg-background border-b border-border/60">
        {/* ── DESKTOP header (single row) ── */}
        {/* header interno: ferramentas do modo ESCREVER — Universo/Estrutura tem chrome proprio */}
        {workspaceTab === 'escrever' && (
        <div className="relative hidden md:flex h-14 items-center justify-between px-4">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => setMetadataOpen(true)}
            className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground truncate max-w-[180px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            {project.metadata.title || t('projects.untitled')}
          </button>

        {/* Presence — junto ao título, à esquerda; o centro pertence às abas
            (só em telas largas, pra nunca alcançar as abas centradas) */}
        <div className="hidden xl:flex items-center -space-x-2 shrink-0">
          <div className="relative h-7 w-7" title={t('editorView.presenceYou', '{{name}} (Você)', { name: user?.name ?? 'Você' })}>
            <div className="h-7 w-7 overflow-hidden rounded-full ring-2 ring-card shadow-sm">
              {user?.avatarUrl ? (
                <img src={user.avatarUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary to-accent text-[10px] font-bold text-primary-foreground">
                  {(user?.name ?? 'V').split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)}
                </div>
              )}
            </div>
            {/* Status dot sits OUTSIDE the clipped avatar so it isn't cut off */}
            <span className="absolute -bottom-0.5 -right-0.5 z-10 h-2 w-2 rounded-full ring-2 ring-card bg-[color:var(--aurora)]" />
          </div>
          {collaborationInfo?.remoteUsers.slice(0, 4).map((u) => (
            <div
              key={u.clientId}
              title={u.name}
              className="relative flex h-7 w-7 items-center justify-center overflow-hidden rounded-full text-[10px] font-bold text-white ring-2 ring-card shadow-sm"
              style={{ backgroundColor: u.color }}
            >
              {u.avatarUrl ? (
                <img src={u.avatarUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                u.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
              )}
            </div>
          ))}
        </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Core editor actions grouped in a pill (inspired by the mock) */}
          <div className="flex items-center gap-0.5 rounded-xl border border-border/50 bg-muted/50 p-1">
          <button
            type="button"
            onClick={() => setGoalModalOpen(true)}
            className="hidden sm:inline-flex items-center justify-center w-8 h-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted active:bg-muted/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            title={t('editor.writingGoal', 'Meta de Escrita')}
            aria-label={t('editor.writingGoal', 'Meta de Escrita')}
          >
            <Target size={17} />
          </button>
          <button
            type="button"
            onClick={() => setNotesModalOpen(true)}
            className="hidden sm:inline-flex items-center justify-center w-8 h-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted active:bg-muted/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            title={t('editor.chapterNotes', 'Notas do Capítulo')}
            aria-label={t('editor.chapterNotes', 'Notas do Capítulo')}
          >
            <StickyNote size={17} />
          </button>

          {/* Subtle divider before find/AI cluster */}
          <div role="separator" aria-orientation="vertical" className="hidden sm:block w-px h-5 bg-border/70 mx-1" />

          <button
            type="button"
            onClick={() => setFindReplaceOpen(!findReplaceOpen)}
            className={cn(
              'inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              findReplaceOpen
                ? 'bg-muted text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted active:bg-muted/80'
            )}
            title={t('editor.findReplace', 'Localizar e Substituir (Ctrl+F)')}
            aria-label={t('editor.findReplace', 'Localizar e Substituir')}
            aria-pressed={findReplaceOpen}
          >
            <GiArchiveResearch size={17} />
          </button>
          <button
            type="button"
            onClick={() => {
              handleSelectionChange();
              setAiChatOpen(!aiChatOpen);
            }}
            className={cn(
              'inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              aiChatOpen
                ? 'bg-primary/15 text-primary'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted active:bg-muted/80'
            )}
            title={t('editor.aiAssistant', 'Assistente de IA')}
            aria-label={t('editor.aiAssistant', 'Assistente de IA')}
            aria-pressed={aiChatOpen}
          >
            <Sparkles size={17} />
          </button>

          {/* Comments toggle — at the top too (count reflects real open comments) */}
          <button
            type="button"
            onClick={() => setRightSidebarOpen(!rightSidebarOpen)}
            className={cn(
              'relative inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              rightSidebarOpen
                ? 'bg-muted text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted active:bg-muted/80'
            )}
            title={t('editor.comments', 'Comentários')}
            aria-label={t('editor.comments', 'Comentários')}
            aria-pressed={rightSidebarOpen}
          >
            <MessageSquare size={17} />
            {openComments.length > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-accent px-0.5 text-[9px] font-bold text-accent-foreground">
                {openComments.length}
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => setVersionHistoryOpen(!versionHistoryOpen)}
            className={cn(
              'hidden sm:inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              versionHistoryOpen
                ? 'bg-muted text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted active:bg-muted/80'
            )}
            title={t('editorView.versionHistory', 'Histórico de versões')}
            aria-label={t('editorView.versionHistory', 'Histórico de versões')}
            aria-pressed={versionHistoryOpen}
          >
            <History size={17} />
          </button>

          {/* Text settings (font · size · spacing · toggles) behind a gear */}
          <TextSettingsPopover settings={settings} update={updateSettings} />

          {/* Keyboard shortcuts overlay ("?") */}
          <ShortcutsHelpButton className="hidden sm:inline-flex" />

          </div>

          <button
            type="button"
            onClick={() => setShareModalOpen(true)}
            className="hidden sm:inline-flex items-center justify-center w-8 h-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted active:bg-muted/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            title={t('editor.share', 'Compartilhar')}
            aria-label={t('editor.share', 'Compartilhar')}
          >
            <Share2 size={17} />
          </button>
          {/* Mobile: everything hidden on small screens, in one "⋯" menu (full parity) */}
          <DropdownMenu>
            <DropdownMenuTrigger
              className="sm:hidden inline-flex items-center justify-center w-8 h-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted active:bg-muted/80 transition-colors"
              title={t('editorView.moreActions', 'Mais ações')}
              aria-label={t('editorView.moreActions', 'Mais ações')}
            >
              <MoreVertical size={18} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[210px]">
              <DropdownMenuItem onClick={() => setGoalModalOpen(true)}>
                <Target size={15} className="text-muted-foreground" />
                {t('editor.writingGoal', 'Meta de escrita')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setNotesModalOpen(true)}>
                <StickyNote size={15} className="text-muted-foreground" />
                {t('editor.chapterNotes', 'Notas do capítulo')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setVersionHistoryOpen(true)}>
                <History size={15} className="text-muted-foreground" />
                {t('editorView.versionHistory', 'Histórico de versões')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShareModalOpen(true)}>
                <Share2 size={15} className="text-muted-foreground" />
                {t('editor.share', 'Compartilhar')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/app/export/${projectId}`)}>
                <Download size={15} className="text-muted-foreground" />
                {t('editor.publish', 'Publicar')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            type="button"
            onClick={() => navigate(`/app/export/${projectId}`)}
            className="hidden md:inline-flex items-center gap-1.5 ml-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('editor.publish', 'Publicar')}
          </button>
        </div>
        </div>
        )}

        {/* ── Troca de workspace (Editor · Universo · Estrutura) — ACIMA dos
            ícones, logo sob o header; visível nas 3 abas (md:hidden). */}
        <div className="md:hidden shrink-0 flex items-center gap-0.5 border-b border-border/60 bg-background px-2 py-1.5">
        {([
          { id: 'escrever' as const, label: t('editorView.tabEditor', 'Editor'), Icon: GiScrollQuill },
          { id: 'biblia' as const, label: t('editorView.tabUniverse', 'Universo'), Icon: GiSpellBook },
          { id: 'estrutura' as const, label: t('editorView.tabStructure', 'Estrutura'), Icon: GiScrollUnfurled },
        ]).map(({ id, label, Icon }) => {
          const active = workspaceTab === id;
          return (
            <button
              key={id}
              onClick={() => handleWorkspaceTabChange(id)}
              className={cn(
                'relative flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg font-mono text-[9px] uppercase tracking-[0.16em] whitespace-nowrap transition-colors',
                active ? 'text-primary' : 'text-muted-foreground'
              )}
            >
              {active && (
                <motion.span
                  layoutId="ws-tab-pill-m"
                  className="absolute inset-0 rounded-lg bg-primary/10 ring-1 ring-inset ring-primary/30"
                  transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                />
              )}
              <Icon size={12} className="relative z-10" />
              <span className="relative z-10">{label}</span>
            </button>
          );
        })}
      </div>
      </header>

      {/* Editor Content Area — workspace tab switches between writing, Bible, and Outline */}
      {workspaceTab !== 'escrever' ? (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden pb-[env(safe-area-inset-bottom)]">
          {workspaceTab === 'biblia' && <StoryBibleView projectId={projectId ?? ''} />}
          {workspaceTab === 'estrutura' && <OutlineBoardView projectId={projectId ?? ''} />}
        </div>
      ) : (
      <div className="flex-1 flex overflow-hidden">
        {/* Main Editor */}
        <div className="flex-1 flex flex-col relative overflow-hidden bg-background">
          {/* Writing Canvas */}
          {chapter ? (
            <ChapterEditor
              chapter={chapter}
              projectId={projectId ?? ''}
              chapterNumber={chapter.sectionType === 'chapter' && chapterNumber ? chapterNumber : undefined}
              onContentChange={handleContentChange}
              onTitleChange={handleTitleChange}
              onImageChange={handleImageChange}
              focusMode={focusMode}
              onEditorReady={handleEditorReady}
              onAddComment={async (commentText) => {
                if (!currentChapterId) return;
                await addComment({ chapterId: currentChapterId, content: commentText, type: 'comment' });
                setRightSidebarOpen(true);
              }}
              onToggleComments={() => setRightSidebarOpen(!rightSidebarOpen)}
              commentsOpen={rightSidebarOpen}
              commentCount={openComments.length}
              saveStatus={saveStatus}
              onSelectionChange={handleEditorSelectionChange}
              siblingTitles={project.chapters
                .filter((c) => c.id !== chapter.id)
                .map((c) => c.title)}
              collaboration={
                collaborationInfo
                  ? {
                      extensions: collaborationInfo.extensions,
                      remoteUsers: collaborationInfo.remoteUsers,
                      connected: collaborationInfo.connected,
                      provider: collaborationInfo.provider,
                    }
                  : undefined
              }
            />
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <p className="font-serif text-lg text-muted-foreground mb-2">
                  {t('editor.selectChapter', 'Selecione um capítulo para começar a escrever')}
                </p>
                <p className="text-sm text-muted-foreground/60">
                  {t('editor.selectChapterHint', 'Escolha na barra lateral à esquerda')}
                </p>
              </div>
            </div>
          )}


          {/* Find & Replace — bottom sheet on mobile, floating panel on desktop */}
          <AnimatePresence>
            {/* Backdrop for Find/Replace — mobile only */}
            {findReplaceOpen && isMobile && createPortal(
              <div
                className="fixed inset-0 z-40 bg-black/40"
                onClick={() => setFindReplaceOpen(false)}
              />,
              document.body
            )}
            {findReplaceOpen && (
              <motion.div
                initial={isMobile ? { y: '100%' } : { opacity: 0, y: -20 }}
                animate={isMobile ? { y: 0 } : { opacity: 1, y: 0 }}
                exit={isMobile ? { y: '100%' } : { opacity: 0, y: -20 }}
                transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
                // Drag-to-dismiss — mobile only, initiated from the handle bar
                drag={isMobile ? 'y' : false}
                dragControls={findReplaceDragControls}
                dragListener={false}
                dragConstraints={{ top: 0, bottom: 0 }}
                dragElastic={{ top: 0, bottom: 0.6 }}
                onDragEnd={(_, info) => {
                  if (info.offset.y > 120 || info.velocity.y > 500) setFindReplaceOpen(false);
                }}
                className={cn(
                  isMobile
                    ? 'fixed inset-x-0 bottom-0 z-50'
                    : 'absolute top-12 right-4 z-20'
                )}
              >
                {/* Invisible drag-capture zone over the existing handle bar (mobile) */}
                {isMobile && (
                  <div
                    className="absolute top-0 inset-x-0 h-6 z-10 touch-none cursor-grab active:cursor-grabbing"
                    onPointerDown={(e) => findReplaceDragControls.start(e)}
                  />
                )}
                <FindReplace
                  editor={editorRef.current}
                  onClose={() => setFindReplaceOpen(false)}
                />
              </motion.div>
            )}
          </AnimatePresence>


        </div>

        {/* AI Chat Sidebar — Drawer on mobile, slide-in panel on desktop */}
        {isMobile ? (
          <Drawer
            open={aiChatOpen && !!chapter && !!project}
            onOpenChange={(o) => { if (!o) setAiChatOpen(false); }}
          >
            <DrawerContent className="max-h-[90dvh] p-0 bg-card">
              <DrawerTitle className="sr-only">{t('editorView.aiAssistant', 'Assistente de IA')}</DrawerTitle>
              {chapter && project && (
                <AIChat
                  embedded
                  chapterContent={chapter.content}
                  selection={selectedText}
                  onApplyAction={handleApplyAIAction}
                  onClose={() => setAiChatOpen(false)}
                  projectId={project.id}
                  currentChapterId={currentChapterId || ''}
                  chapters={project.chapters.map((c) => ({
                    id: c.id,
                    title: c.title,
                    sectionType: c.sectionType,
                    content: c.content,
                  }))}
                  totalWords={project.chapters.reduce(
                    (acc, c) => acc + countWords(c.content || ''),
                    0
                  )}
                  onCreateChapter={handleCreateChapter}
                  onUpdateChapter={handleUpdateChapterTitle}
                  onDeleteChapter={handleDeleteChapterById}
                  onReorderChapters={handleReorderChaptersById}
                  onSelectChapter={setCurrentChapter}
                />
              )}
            </DrawerContent>
          </Drawer>
        ) : (
          <AnimatePresence>
            {aiChatOpen && chapter && project && (
              <AIChat
                chapterContent={chapter.content}
                selection={selectedText}
                onApplyAction={handleApplyAIAction}
                onClose={() => setAiChatOpen(false)}
                projectId={project.id}
                currentChapterId={currentChapterId || ''}
                chapters={project.chapters.map((c) => ({
                  id: c.id,
                  title: c.title,
                  sectionType: c.sectionType,
                  content: c.content,
                }))}
                totalWords={project.chapters.reduce(
                  (acc, c) => acc + countWords(c.content || ''),
                  0
                )}
                onCreateChapter={handleCreateChapter}
                onUpdateChapter={handleUpdateChapterTitle}
                onDeleteChapter={handleDeleteChapterById}
                onReorderChapters={handleReorderChaptersById}
                onSelectChapter={setCurrentChapter}
              />
            )}
          </AnimatePresence>
        )}

        {/* Version History Panel — Drawer on mobile, slide-in panel on desktop */}
        {isMobile ? (
          <Drawer
            open={versionHistoryOpen && !!currentChapterId}
            onOpenChange={(o) => { if (!o) setVersionHistoryOpen(false); }}
          >
            <DrawerContent className="max-h-[90dvh] p-0 bg-card">
              <DrawerTitle className="sr-only">{t('editorView.versionHistory', 'Histórico de versões')}</DrawerTitle>
              {currentChapterId && (
                <VersionHistoryPanel
                  embedded
                  chapterId={currentChapterId}
                  onClose={() => setVersionHistoryOpen(false)}
                  onRestored={handleVersionRestored}
                />
              )}
            </DrawerContent>
          </Drawer>
        ) : (
          <AnimatePresence>
            {versionHistoryOpen && currentChapterId && (
              <VersionHistoryPanel
                chapterId={currentChapterId}
                onClose={() => setVersionHistoryOpen(false)}
                onRestored={handleVersionRestored}
              />
            )}
          </AnimatePresence>
        )}

        {/* Backdrop for Comments sidebar — mobile only */}
        {rightSidebarOpen && isMobile && createPortal(
          <div
            className="fixed inset-0 z-40 bg-black/40"
            onClick={() => setRightSidebarOpen(false)}
          />,
          document.body
        )}

        {/* Comments/Suggestions Sidebar — bottom sheet on mobile, side panel on desktop */}
        <AnimatePresence>
          {rightSidebarOpen && (
            <motion.div
              initial={isMobile ? { y: '100%' } : { x: '100%', opacity: 0 }}
              animate={isMobile ? { y: 0 } : { x: 0, opacity: 1 }}
              exit={isMobile ? { y: '100%' } : { x: '100%', opacity: 0 }}
              transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
              // Drag-to-dismiss — mobile only, initiated from the handle bar
              drag={isMobile ? 'y' : false}
              dragControls={commentsDragControls}
              dragListener={false}
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.6 }}
              onDragEnd={(_, info) => {
                if (info.offset.y > 120 || info.velocity.y > 500) setRightSidebarOpen(false);
              }}
              className={cn(
                'flex flex-col overflow-hidden border-border bg-card',
                isMobile
                  ? 'fixed inset-x-0 bottom-0 z-50 h-auto max-h-[90dvh] rounded-t-2xl border-t shadow-2xl'
                  : 'relative w-80 flex-shrink-0 border-l'
              )}
            >
              {isMobile && (
                <div
                  className="mx-auto mt-2 mb-1 h-1.5 w-10 shrink-0 rounded-full bg-border cursor-grab active:cursor-grabbing touch-none"
                  onPointerDown={(e) => commentsDragControls.start(e)}
                />
              )}
              <div className="h-10 flex items-center px-4 gap-4 border-b border-border flex-shrink-0">
                <button
                  className={cn(
                    'text-sm font-medium py-2 border-b-2 transition-colors',
                    activeTab === 'comments'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  )}
                  onClick={() => setActiveTab('comments')}
                >
                  {t('editor.comments', 'Comentários')}
                </button>
                <button
                  className={cn(
                    'text-sm font-medium py-2 border-b-2 transition-colors',
                    activeTab === 'suggestions'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  )}
                  onClick={() => setActiveTab('suggestions')}
                >
                  {t('editor.suggestions', 'Sugestões')}
                </button>
                <div className="flex-1" />
                <button
                  onClick={() => setRightSidebarOpen(false)}
                  className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4 bg-muted/30">
                {/* Open Comments */}
                {openComments.length === 0 && resolvedComments.length === 0 && (
                  <div className="text-center py-8">
                    <MessageSquare size={32} className="mx-auto text-muted-foreground/30 mb-2" />
                    <p className="text-sm text-muted-foreground">{t('editorView.noCommentsYet', 'Nenhum comentário ainda')}</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">{t('editorView.addCommentToStart', 'Adicione um comentário para iniciar uma discussão')}</p>
                  </div>
                )}

                {openComments.map((comment) => (
                  <div key={comment.id} className="p-4 rounded-xl bg-card border border-accent/30 shadow-sm">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-semibold">
                          {getUserInitials(comment.user.name)}
                        </div>
                        <div>
                          <span className="text-sm font-medium text-foreground">{comment.user.name}</span>
                          {comment.user.id !== user?.id && (
                            <span className="text-xs text-muted-foreground ml-1">{t('editorView.external', '(Externo)')}</span>
                          )}
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground">{formatTimeAgo(comment.createdAt)}</span>
                    </div>

                    {comment.selectedText && (
                      <p className="text-sm text-muted-foreground italic pl-3 border-l-2 border-accent/30 mb-3">
                        "{comment.selectedText}"
                      </p>
                    )}

                    <div className="p-3 rounded-lg bg-accent/5 border border-accent/10 text-sm text-foreground mb-3">
                      {comment.content}
                    </div>

                    {/* Replies */}
                    {comment.replies && comment.replies.length > 0 && (
                      <div className="ml-4 mt-3 space-y-3 border-l-2 border-border pl-3">
                        {comment.replies.map((reply) => (
                          <div key={reply.id} className="text-sm">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-muted-foreground text-[10px] font-semibold">
                                {getUserInitials(reply.user.name)}
                              </div>
                              <span className="font-medium text-xs">{reply.user.name}</span>
                              <span className="text-xs text-muted-foreground">{formatTimeAgo(reply.createdAt)}</span>
                            </div>
                            <p className="text-muted-foreground pl-7">{reply.content}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Reply input */}
                    {replyingTo === comment.id ? (
                      <div className="flex gap-2 mt-3">
                        <input
                          type="text"
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleReply(comment.id)}
                          placeholder={t('editorView.writeAReply', 'Escreva uma resposta...')}
                          className="flex-1 text-sm px-3 py-1.5 rounded-lg bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
                          autoFocus
                        />
                        <button
                          onClick={() => handleReply(comment.id)}
                          disabled={!replyText.trim()}
                          className="p-2 rounded-lg bg-primary text-primary-foreground disabled:opacity-50"
                        >
                          <Send size={14} />
                        </button>
                        <button
                          onClick={() => { setReplyingTo(null); setReplyText(''); }}
                          className="p-2 rounded-lg text-muted-foreground hover:bg-muted"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={() => setReplyingTo(comment.id)}
                          className="flex-1 text-xs font-medium py-2 rounded-lg bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors flex items-center justify-center gap-1"
                        >
                          <CornerDownRight size={12} /> {t('editor.reply', 'Responder')}
                        </button>
                        <button
                          onClick={() => resolveComment(comment.id)}
                          className="flex-1 text-xs font-medium py-2 rounded-lg bg-[rgba(94,234,212,0.1)] text-[#1F8A76] dark:text-[color:var(--aurora)] hover:bg-[rgba(94,234,212,0.2)] flex items-center justify-center gap-1 transition-colors"
                        >
                          <Check size={14} /> {t('editor.resolve', 'Resolver')}
                        </button>
                      </div>
                    )}
                  </div>
                ))}

                {/* Resolved Comments */}
                {resolvedComments.length > 0 && (
                  <>
                    <div className="flex items-center gap-2 pt-2">
                      <div className="flex-1 h-px bg-border" />
                      <span className="text-xs text-muted-foreground px-2">{t('editorView.resolved', 'Resolvidos')}</span>
                      <div className="flex-1 h-px bg-border" />
                    </div>
                    {resolvedComments.map((comment) => (
                      <div key={comment.id} className="p-4 rounded-xl bg-card border border-border opacity-60 hover:opacity-100 transition-opacity">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-muted-foreground text-xs font-semibold">
                              {getUserInitials(comment.user.name)}
                            </div>
                            <span className="text-sm font-medium text-foreground">{comment.user.name}</span>
                          </div>
                          <span className="text-xs text-muted-foreground">{formatTimeAgo(comment.createdAt)}</span>
                        </div>
                        {comment.selectedText && (
                          <p className="text-sm line-through text-muted-foreground italic pl-3 border-l-2 border-border mb-2">
                            "{comment.selectedText}"
                          </p>
                        )}
                        <p className="text-sm text-muted-foreground mb-2">{comment.content}</p>
                        <div className="flex items-center text-xs font-medium text-[#1F8A76] dark:text-[color:var(--aurora)]">
                          <Check size={14} className="mr-1" />
                          {comment.resolver
                            ? t('editorView.resolvedBy', 'Resolvido por {{name}}', { name: comment.resolver.name })
                            : t('editor.resolvedByYou', 'Resolvido')}
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>

              {/* Comment input */}
              <div className="p-4 border-t border-border flex-shrink-0">
                <div className="relative">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                    placeholder={t('editor.addComment', 'Adicionar um comentário...')}
                    className="w-full rounded-full pl-4 pr-10 py-2.5 text-sm bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full text-primary hover:bg-primary/10 transition-colors disabled:opacity-50"
                  >
                    <Send size={16} />
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      )} {/* end workspace tab conditional */}
      {/* ── Barra de status (● Salvo · palavras · Nv+XP) — ÚLTIMO item ──
          Fica ABAIXO da tab-bar do workspace (no mobile as tabs ficam acima
          dela). Só no 'escrever'. No mobile ela é que respeita a safe-area
          (é o rodapé real); no desktop a tab-bar é md:hidden, então segue
          sendo o último item como antes. */}
      {workspaceTab === 'escrever' && (
        <div className="flex items-center gap-2 md:gap-5 px-3 md:px-5 py-2 border-t border-border/60 bg-background font-mono text-[9px] md:text-[9.5px] uppercase tracking-[0.14em] text-muted-foreground select-none flex-shrink-0 overflow-hidden pb-[calc(0.5rem+env(safe-area-inset-bottom))] md:pb-2">
            <span className="flex items-center gap-2 shrink-0" style={{ color: saveStatus === 'unsaved' ? 'var(--ember)' : 'var(--aurora)' }}>
              <i
                key={saveStatus}
                className="save-ping w-1.5 h-1.5 rounded-full shrink-0"
                style={{
                  background: saveStatus === 'unsaved' ? 'var(--ember)' : 'var(--aurora)',
                  boxShadow: `0 0 8px ${saveStatus === 'unsaved' ? 'var(--ember)' : 'var(--aurora)'}`,
                }}
              />
              {saveStatus === 'saved'
                ? t('editor.saved', 'Salvo')
                : saveStatus === 'saving'
                  ? t('editor.saving', 'Salvando...')
                  : t('editor.unsaved', 'Não salvo')}
            </span>
            <span className="shrink-0">{totalWords.toLocaleString()} {t('projects.words', 'palavras')}</span>
            {chapter && settings.showWordCount && (
              <span className="hidden lg:inline shrink-0">
                {t('editor.chapterEyebrow', 'Capítulo')} · {chapter.wordCount.toLocaleString()}
              </span>
            )}
            {settings.writingEffect !== 'none' && writingEffectLabel && (
              <span className="hidden md:inline shrink-0" style={{ color: 'var(--ember)' }}>✦ {writingEffectLabel}</span>
            )}
            <span className="ml-auto flex items-center gap-1.5 md:gap-2.5 shrink-0">
              <span className="shrink-0" style={{ color: 'var(--ember)' }}>{t('hud.lvlAbbrev', 'Nv')} {writerLvl.level}</span>
              <span className="xp-track w-10 md:w-[110px] shrink-0">
                <span className="xp-fill block" style={{ width: `${writerLvl.pct}%` }} />
              </span>
              <span className="hidden md:inline">{writerLvl.xpIn.toLocaleString()}/{LEVEL_XP.toLocaleString()}</span>
            </span>
          </div>
      )}


      {/* Modals */}
      <BookMetadataModal
        project={project}
        open={metadataOpen}
        onOpenChange={setMetadataOpen}
      />

      <WritingGoalModal
        projectId={project.id}
        currentGoal={project.writingGoal}
        open={goalModalOpen}
        onOpenChange={setGoalModalOpen}
      />

      <ChapterNotesModal
        chapter={chapter}
        onSave={handleNotesUpdate}
        open={notesModalOpen && !!chapter}
        onOpenChange={setNotesModalOpen}
      />

      <ShareModal
        open={shareModalOpen}
        onOpenChange={setShareModalOpen}
        projectId={project.id}
        projectTitle={project.metadata.title}
        isOwner={isOwner}
      />

    </div>
  );
}

